// Advanced Technical Indicators for AI Trading
export class TechnicalIndicators {
  // Simple Moving Average
  static SMA(prices, period) {
    if (prices.length < period) return null;
    const sum = prices.slice(-period).reduce((a, b) => a + b, 0);
    return sum / period;
  }

  // Exponential Moving Average
  static EMA(prices, period) {
    if (prices.length < period) return null;
    const multiplier = 2 / (period + 1);
    let ema = prices[0];
    
    for (let i = 1; i < prices.length; i++) {
      ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
    }
    return ema;
  }

  // Relative Strength Index (RSI)
  static RSI(prices, period = 14) {
    if (prices.length < period + 1) return null;
    
    let gains = 0;
    let losses = 0;
    
    for (let i = 1; i <= period; i++) {
      const change = prices[i] - prices[i - 1];
      if (change > 0) gains += change;
      else losses -= change;
    }
    
    const avgGain = gains / period;
    const avgLoss = losses / period;
    
    if (avgLoss === 0) return 100;
    
    const rs = avgGain / avgLoss;
    return 100 - (100 / (1 + rs));
  }

  // MACD (Moving Average Convergence Divergence)
  static MACD(prices, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
    if (prices.length < slowPeriod) return null;
    
    const fastEMA = this.EMA(prices, fastPeriod);
    const slowEMA = this.EMA(prices, slowPeriod);
    
    if (!fastEMA || !slowEMA) return null;
    
    const macdLine = fastEMA - slowEMA;
    
    // For signal line, we'd need more historical data
    // This is a simplified version
    return {
      macd: macdLine,
      signal: macdLine * 0.9, // Simplified signal line
      histogram: macdLine - (macdLine * 0.9)
    };
  }

  // Bollinger Bands
  static BollingerBands(prices, period = 20, stdDev = 2) {
    if (prices.length < period) return null;
    
    const sma = this.SMA(prices, period);
    if (!sma) return null;
    
    const recentPrices = prices.slice(-period);
    const variance = recentPrices.reduce((sum, price) => sum + Math.pow(price - sma, 2), 0) / period;
    const standardDeviation = Math.sqrt(variance);
    
    return {
      upper: sma + (standardDeviation * stdDev),
      middle: sma,
      lower: sma - (standardDeviation * stdDev),
      bandwidth: (standardDeviation * stdDev * 2) / sma
    };
  }

  // Stochastic Oscillator
  static Stochastic(highs, lows, closes, kPeriod = 14, dPeriod = 3) {
    if (highs.length < kPeriod) return null;
    
    const recentHighs = highs.slice(-kPeriod);
    const recentLows = lows.slice(-kPeriod);
    const recentCloses = closes.slice(-kPeriod);
    
    const highestHigh = Math.max(...recentHighs);
    const lowestLow = Math.min(...recentLows);
    
    const k = ((recentCloses[recentCloses.length - 1] - lowestLow) / (highestHigh - lowestLow)) * 100;
    
    return {
      k: k,
      d: k // Simplified D line
    };
  }

  // Williams %R
  static WilliamsR(highs, lows, closes, period = 14) {
    if (highs.length < period) return null;
    
    const recentHighs = highs.slice(-period);
    const recentLows = lows.slice(-period);
    const recentCloses = closes.slice(-period);
    
    const highestHigh = Math.max(...recentHighs);
    const lowestLow = Math.min(...recentLows);
    
    return ((highestHigh - recentCloses[recentCloses.length - 1]) / (highestHigh - lowestLow)) * -100;
  }

  // Average True Range (ATR)
  static ATR(highs, lows, closes, period = 14) {
    if (highs.length < period + 1) return null;
    
    const trueRanges = [];
    for (let i = 1; i < highs.length; i++) {
      const tr1 = highs[i] - lows[i];
      const tr2 = Math.abs(highs[i] - closes[i - 1]);
      const tr3 = Math.abs(lows[i] - closes[i - 1]);
      trueRanges.push(Math.max(tr1, tr2, tr3));
    }
    
    return this.SMA(trueRanges, period);
  }

  // Commodity Channel Index (CCI)
  static CCI(highs, lows, closes, period = 20) {
    if (highs.length < period) return null;
    
    const recentHighs = highs.slice(-period);
    const recentLows = lows.slice(-period);
    const recentCloses = closes.slice(-period);
    
    const typicalPrices = recentHighs.map((high, i) => (high + recentLows[i] + recentCloses[i]) / 3);
    const sma = this.SMA(typicalPrices, period);
    
    const meanDeviation = typicalPrices.reduce((sum, tp) => sum + Math.abs(tp - sma), 0) / period;
    
    const currentTP = (recentHighs[recentHighs.length - 1] + recentLows[recentLows.length - 1] + recentCloses[recentCloses.length - 1]) / 3;
    
    return (currentTP - sma) / (0.015 * meanDeviation);
  }

  // Generate comprehensive technical analysis
  static analyze(prices, highs = null, lows = null) {
    if (!prices || prices.length < 20) return null;
    
    // Use prices as highs/lows if not provided (simplified)
    const highPrices = highs || prices;
    const lowPrices = lows || prices;
    
    const analysis = {
      sma20: this.SMA(prices, 20),
      sma50: this.SMA(prices, 50),
      ema12: this.EMA(prices, 12),
      ema26: this.EMA(prices, 26),
      rsi: this.RSI(prices, 14),
      macd: this.MACD(prices),
      bollinger: this.BollingerBands(prices, 20, 2),
      stochastic: this.Stochastic(highPrices, lowPrices, prices),
      williamsR: this.WilliamsR(highPrices, lowPrices, prices),
      atr: this.ATR(highPrices, lowPrices, prices),
      cci: this.CCI(highPrices, lowPrices, prices)
    };

    // Generate signals
    analysis.signals = this.generateSignals(analysis);
    
    return analysis;
  }

  // Generate trading signals based on technical indicators
  static generateSignals(analysis) {
    const signals = {
      trend: 'NEUTRAL',
      momentum: 'NEUTRAL',
      volatility: 'NORMAL',
      strength: 0,
      recommendations: []
    };

    // Trend analysis
    if (analysis.sma20 > analysis.sma50) {
      signals.trend = 'BULLISH';
      signals.strength += 1;
    } else if (analysis.sma20 < analysis.sma50) {
      signals.trend = 'BEARISH';
      signals.strength -= 1;
    }

    // RSI momentum
    if (analysis.rsi > 70) {
      signals.momentum = 'OVERBOUGHT';
      signals.recommendations.push('Consider selling - RSI overbought');
    } else if (analysis.rsi < 30) {
      signals.momentum = 'OVERSOLD';
      signals.recommendations.push('Consider buying - RSI oversold');
      signals.strength += 1;
    }

    // MACD signals
    if (analysis.macd && analysis.macd.macd > analysis.macd.signal) {
      signals.strength += 1;
      signals.recommendations.push('MACD bullish crossover');
    } else if (analysis.macd && analysis.macd.macd < analysis.macd.signal) {
      signals.strength -= 1;
      signals.recommendations.push('MACD bearish crossover');
    }

    // Bollinger Bands
    if (analysis.bollinger) {
      const currentPrice = analysis.sma20; // Simplified
      if (currentPrice > analysis.bollinger.upper) {
        signals.volatility = 'HIGH';
        signals.recommendations.push('Price above upper Bollinger Band - potential reversal');
      } else if (currentPrice < analysis.bollinger.lower) {
        signals.volatility = 'HIGH';
        signals.recommendations.push('Price below lower Bollinger Band - potential bounce');
        signals.strength += 1;
      }
    }

    // Stochastic
    if (analysis.stochastic && analysis.stochastic.k > 80) {
      signals.recommendations.push('Stochastic overbought');
    } else if (analysis.stochastic && analysis.stochastic.k < 20) {
      signals.recommendations.push('Stochastic oversold');
      signals.strength += 1;
    }

    // Williams %R
    if (analysis.williamsR > -20) {
      signals.recommendations.push('Williams %R overbought');
    } else if (analysis.williamsR < -80) {
      signals.recommendations.push('Williams %R oversold');
      signals.strength += 1;
    }

    return signals;
  }
}

